/**
 * CR104: Merchant Combobox
 * Allows selecting existing merchant OR creating a new one
 */

import { useState, useMemo } from "react";
import { Check, ChevronsUpDown, Plus, Building2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { normalizeMerchantId } from "@/lib/utils/merchantIdNormalization";
import { getCategoryLabel } from "@/lib/config/categories";

export interface MerchantOption {
  merchant_id: string;       // merchant_key (text slug)
  merchant_uuid: string;     // merchants.id (UUID)
  merchant_name: string;
  category?: string | null;
}

export interface MerchantSelection {
  merchant_id: string;       // merchant_key (text slug)
  merchant_uuid: string;     // merchants.id (UUID) — empty string for new merchants
  merchant_name: string;
  isNew: boolean;
  existingCategory?: string | null;
}

interface MerchantComboboxProps {
  merchants: MerchantOption[];
  value: MerchantSelection | null;
  onChange: (selection: MerchantSelection | null) => void;
  disabled?: boolean;
}

export function MerchantCombobox({
  merchants,
  value,
  onChange,
  disabled,
}: MerchantComboboxProps) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  // Filter merchants based on search
  const filteredMerchants = useMemo(() => {
    if (!searchValue.trim()) return merchants.slice(0, 50); // Show first 50 when empty
    
    const search = searchValue.toLowerCase();
    return merchants
      .filter(
        (m) =>
          m.merchant_id.toLowerCase().includes(search) ||
          m.merchant_name.toLowerCase().includes(search)
      )
      .slice(0, 50);
  }, [merchants, searchValue]);

  // Check if search matches any existing merchant exactly
  const normalizedSearch = normalizeMerchantId(searchValue);
  const exactMatch = useMemo(() => {
    return merchants.find(
      (m) => m.merchant_id === normalizedSearch || 
             m.merchant_name.toLowerCase() === searchValue.toLowerCase().trim()
    );
  }, [merchants, normalizedSearch, searchValue]);

  // Show "Create new" option when search doesn't exactly match an existing merchant
  const showCreateNew = searchValue.trim().length > 0 && !exactMatch && normalizedSearch.length > 0;

  const handleSelectExisting = (merchant: MerchantOption) => {
    onChange({
      merchant_id: merchant.merchant_id,
      merchant_uuid: merchant.merchant_uuid,
      merchant_name: merchant.merchant_name,
      isNew: false,
      existingCategory: merchant.category,
    });
    setOpen(false);
    setSearchValue("");
  };

  const handleCreateNew = () => {
    const displayName = searchValue.trim();
    const merchantId = normalizedSearch;
    
    onChange({
      merchant_id: merchantId,
      merchant_uuid: "",  // Will be set after merchant creation
      merchant_name: displayName,
      isNew: true,
      existingCategory: null,
    });
    setOpen(false);
    setSearchValue("");
  };

  const displayValue = value
    ? value.isNew
      ? `${value.merchant_name} (new)`
      : value.merchant_name
    : "Select merchant...";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between font-normal"
        >
          <span className="truncate">{displayValue}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search or type new merchant name..."
            value={searchValue}
            onValueChange={setSearchValue}
          />
          <CommandList>
            <CommandEmpty>
              {searchValue.trim() ? (
                <div className="py-2 px-3 text-sm text-muted-foreground">
                  No matching merchants found
                </div>
              ) : (
                <div className="py-2 px-3 text-sm text-muted-foreground">
                  Start typing to search or create
                </div>
              )}
            </CommandEmpty>
            
            {/* Create New Option */}
            {showCreateNew && (
              <>
                <CommandGroup heading="Create New">
                  <CommandItem
                    onSelect={handleCreateNew}
                    className="cursor-pointer"
                  >
                    <Plus className="mr-2 h-4 w-4 text-primary" />
                    <div className="flex flex-col">
                      <span className="font-medium">
                        Create "{searchValue.trim()}"
                      </span>
                      <span className="text-xs text-muted-foreground">
                        Key: {normalizedSearch}
                      </span>
                    </div>
                  </CommandItem>
                </CommandGroup>
                <CommandSeparator />
              </>
            )}

            {/* Existing Merchants */}
            {filteredMerchants.length > 0 && (
              <CommandGroup heading="Existing Merchants">
                {filteredMerchants.map((merchant) => (
                  <CommandItem
                    key={merchant.merchant_id}
                    value={merchant.merchant_id}
                    onSelect={() => handleSelectExisting(merchant)}
                    className="cursor-pointer"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value?.merchant_id === merchant.merchant_id
                          ? "opacity-100"
                          : "opacity-0"
                      )}
                    />
                    <Building2 className="mr-2 h-4 w-4 text-muted-foreground" />
                    <div className="flex flex-1 items-center justify-between">
                      <div className="flex flex-col">
                        <span>{merchant.merchant_name}</span>
                        <span className="text-xs text-muted-foreground">
                          {merchant.merchant_id}
                        </span>
                      </div>
                      {merchant.category ? (
                        <Badge variant="secondary" className="ml-2 text-xs">
                          {getCategoryLabel(merchant.category)}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="ml-2 text-xs text-destructive">
                          No category
                        </Badge>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
